import request from '@/utils/request'

/**
 * 콘솔은 모든 호출에 skipOktaReauth 를 켠다.
 * 이유: @PreAuthorize 거절이 401 + ERROR_UNAUTHORIZED 로 오는데(SecurityConfig 가 403 대신 401 로 매핑),
 * request.ts 의 응답 인터셉터는 그것을 토큰 실패로 오인해 Okta 재인증을 돌린다 → 권한 없는 사용자에게 재인증 루프.
 * 진짜 토큰 만료는 요청 인터셉터가 요청 전에 갱신하므로 여기서 잃는 것이 없다.
 *
 * `request.ts` 의 `MomsApiRequestConfig` 타입은 export 되지 않는다 — 인라인 객체로 넘긴다.
 */
const CONFIG = { skipOktaReauth: true } as const

/**
 * `consoleRequest` — 콘솔 전용 얇은 래퍼. 실제 axios 호출은 전부 어드민 공용 `@/utils/request` 가
 * 맡고, 여기서는 `CONFIG` 를 매 호출에 강제하는 것과 `request.ts` 의 실제 시그니처(핸드오프 문서의
 * 스케치가 아니라 컨트롤러가 확인한 쪽)에 맞춰 호출부를 좁히는 역할만 한다.
 */
/**
 * `post` 가 받는 호출별 추가 axios 설정. `request.ts` 의 `MomsApiRequestConfig` 와 동일하게
 * export 되지 않는 내부 타입이라 여기서 필요한 필드만 좁혀서 인라인으로 잡는다(LOGISPRD-12506
 * Task 14 — 재발사는 동기 호출·최대 5분이라 전역 axios timeout(100000ms)보다 길게 걸린다.
 * 전역 설정은 어드민 공용이라 건드리지 않고, 이 필드로 호출 하나에만 얹는다).
 */
export type ConsoleRequestExtraConfig = { timeout?: number }

/*
 * 범위 축소(2026-09-15) — `put`·`deleteWithBody` 를 뺐다. 바코드 보정(마스터 상품 PUT · RFID 매핑
 * 삭제)이 사라지면서 호출부가 0 이 됐다. 되살릴 땐 `@/utils/request` 의 `put` / named export
 * `deleteWithBody` 를 `CONFIG` 와 함께 다시 감싸면 된다 — 콘솔의 "모든 호출에 skipOktaReauth"
 * 원칙에 예외를 두지 않는 것이 이 래퍼의 유일한 존재 이유다.
 */
export const consoleRequest = {
  // request.get(url, queryParams?, config?) — queryParams 는 쿼리스트링으로 URL 에 직접 붙는다(Utils.qsStringify).
  get: <T, R = UnknownMap>(url: string, params?: R): Promise<T> => request.get<T, R>(url, params, CONFIG),
  // request.post(url, body, config?). extraConfig 는 CONFIG 아래 깔린다(예: timeout 연장) —
  // 최종 리뷰 작은 것 2 — `{ ...CONFIG, ...extraConfig }` 였을 때는 `extraConfig` 가 **변수**로
  // 넘어오면(예: `skipOktaReauth: false` 를 담은 객체) 객체 리터럴에서만 동작하는 TS 초과 속성
  // 검사가 못 잡아 조용히 CONFIG 를 덮어 skipOktaReauth 가 꺼질 수 있었다. 순서를 뒤집어 CONFIG 를
  // 항상 마지막에 얹으면 extraConfig 가 뭘 담고 있든 skipOktaReauth 는 무조건 true 로 남는다.
  post: <T, R = UnknownMap>(url: string, body?: R, extraConfig?: ConsoleRequestExtraConfig): Promise<T> =>
    request.post<T, R>(url, (body ?? {}) as R, { ...extraConfig, ...CONFIG }),
  // Hold 삭제는 ?propagate= 를 쿼리로 보낸다 — request.delete 는 deleteR(url, id?, queryParams?, config?) 다.
  // id 는 쓰지 않는다(콘솔의 삭제 대상은 전부 쿼리스트링/본문으로 식별한다).
  delete: <T>(url: string, queryParams?: UnknownMap): Promise<T> => request.delete<T>(url, undefined, queryParams, CONFIG),
}
